import { z } from 'zod';
import { modelParamsSchema, modelSeatSchema } from './models.js';
import {
  saveDraftRequestSchema,
  saveVersionRequestSchema,
  versionedContentSchema,
  versionedDetailSchema,
} from './versioning.js';

/**
 * « Les modèles » (vitrine.md section 5, issue #192): the seats of the models
 * and the ceiling per step - what `model_params.params` held in place until the
 * issue - as the SIXTH consumer of the versioning pattern, and the second
 * SINGLETON after the texts of the noyaux (`noyau-texts.ts`).
 *
 * Everything the screen shows beside the form (the first-boot seed, the
 * published tariffs, the cost estimates, the journal of the moves made before
 * the versions) stays in the view of `GET /api/models/params`; what a VERSION
 * carries is the params and nothing else.
 *
 * ## The same patron as the texts of the noyaux
 *
 * One identifier, a constant: a session freezes the id of a version of the
 * singleton and the sync of the vitrine addresses it by it. One fixed name, a
 * literal, so no version renames the singleton (the layer copies `name` onto
 * the entity). Unlike the texts, the singleton is BORN at boot, by the data
 * migration that writes its v1 from `model_params` (`models/settings.ts`), so
 * the detail never answers 404 on a store that has booted.
 *
 * ## What `params.version` is, and is not
 *
 * The shape of `modelParamsSchema` carries a semver the journal of the old
 * moves counted. A version of the singleton keeps the field - the shape is
 * the shape - but NOTHING reads it as the version: the number of the version
 * is. The server answers the params of a version with `version: 'v<number>'`,
 * so the two never disagree on screen.
 */

/** The identifier of the singleton entity - the one `{id}` its routes serve. */
export const MODEL_SETTINGS_ID = 'model-settings';

/** The fixed name the layer wants on the entity and in every version (§4.1). */
export const MODEL_SETTINGS_NAME = 'Les modèles';

/** The fixed name, as a schema: that one string, or nothing and then that one. */
const modelSettingsNameSchema = z.literal(MODEL_SETTINGS_NAME).default(MODEL_SETTINGS_NAME);

/**
 * The CONTENT of one version: the five seats and the ceiling, under the fixed
 * name. Not `.strict()`, like every stored content: a field added later must
 * not make today's versions unreadable (the params keep their own strictness,
 * which is the shape the base has always held).
 */
export const modelSettingsContentSchema = z.object({
  name: modelSettingsNameSchema,
  params: modelParamsSchema,
});
export type ModelSettingsContent = z.infer<typeof modelSettingsContentSchema>;

/**
 * A seat as a DRAFT holds it: the model id may be empty while somebody types
 * one. The version refuses an empty model (`modelSeatSchema`), the autosave of
 * the draft does not - the draft absorbs a partial entry (§4.1).
 */
const draftSeatSchema = modelSeatSchema.innerType().extend({ model: z.string() });

/** The params of a draft: the version's shape with the seats loosened. */
const draftParamsSchema = modelParamsSchema.extend({
  interviewer: draftSeatSchema,
  scribe: draftSeatSchema,
  narrator: draftSeatSchema,
  ranger: draftSeatSchema,
  analyst: draftSeatSchema,
});

/** The content of a DRAFT: the same name, and params a version would still refuse. */
export const modelSettingsDraftContentSchema = z.object({
  name: modelSettingsNameSchema,
  params: draftParamsSchema,
});
export type ModelSettingsDraftContent = z.infer<typeof modelSettingsDraftContentSchema>;

/** Everything the editor of Réglages > Modèles reads in one call. */
export const modelSettingsDetailSchema = versionedDetailSchema(
  modelSettingsContentSchema,
  modelSettingsDraftContentSchema,
);
export type ModelSettingsDetail = z.infer<typeof modelSettingsDetailSchema>;

/** One version read in full, for « Voir » and « Repartir de cette version ». */
export const modelSettingsVersionSchema = versionedContentSchema(modelSettingsContentSchema);
export type ModelSettingsVersion = z.infer<typeof modelSettingsVersionSchema>;

/** « Enregistrer une nouvelle version » of the models. */
export const modelSettingsSaveVersionSchema = saveVersionRequestSchema(modelSettingsContentSchema);
export type ModelSettingsSaveVersion = z.infer<typeof modelSettingsSaveVersionSchema>;

/** An autosave of the draft of the models. */
export const modelSettingsSaveDraftSchema = saveDraftRequestSchema(modelSettingsDraftContentSchema);
export type ModelSettingsSaveDraft = z.infer<typeof modelSettingsSaveDraftSchema>;
